import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { BookOpen, FileJson, Layers, Loader2, Plus, Send, Sparkles } from "lucide-react";
import { CourseModulesEditor, type ModuleLessonOption } from "@/components/CourseModulesEditor";
import {
  COURSE_INTEREST_TAGS,
  COURSE_LEVELS,
  COURSE_STATUS_META,
  errorMessage,
  fetchJson,
  interestTagLabel,
  slugifyTitle,
  type Course,
  type CourseStatus,
} from "@/lib/courses";

type CreatorCourse = Course & { modules: number };

interface CreatorLesson {
  id: string;
  title: string;
  slug: string;
  description?: string | null;
  emoji?: string | null;
  grade?: number;
  subject?: string;
  topic?: string;
  estimatedMinutes?: number;
  xpReward?: number;
  status?: string;
  tags?: string[];
  blocks?: unknown[];
  blockCount?: number;
  updatedAt?: string;
}

const EDITABLE_STATUSES: CourseStatus[] = ["draft", "changes_requested"];

// ---------------------------------------------------------------------------------
// Course form (creator flavour: no category, no subject, no grade range)
// ---------------------------------------------------------------------------------

const creatorCourseSchema = z.object({
  slug: z
    .string()
    .trim()
    .min(1, "Slug is required")
    .max(160, "Slug is too long")
    .regex(/^[a-z0-9-]+$/, "Use lowercase letters, numbers and dashes only"),
  title: z.string().trim().min(1, "Give your course a title").max(160, "Title is too long"),
  description: z.string().trim().min(1, "Tell students what they will learn").max(1000, "Description is too long"),
  emoji: z.string().trim().max(8, "One or two characters is plenty"),
  coverImageUrl: z
    .string()
    .trim()
    .max(500)
    .refine((v) => v === "" || /^https?:\/\//i.test(v), "Must start with http:// or https://"),
  interestTags: z.array(z.string()).min(1, "Pick at least one interest"),
  level: z.enum(["starter", "explorer", "expert"]),
  estimatedMinutes: z.string().trim().regex(/^\d{1,4}$/, "Minutes must be a whole number"),
  credentialTitle: z.string().trim().max(160, "Title is too long"),
  prerequisites: z.array(z.string()),
  sortOrder: z.string().trim().regex(/^-?\d{1,5}$/, "Sort order must be a whole number"),
});

type CreatorCourseValues = z.infer<typeof creatorCourseSchema>;

const EMPTY_COURSE: CreatorCourseValues = {
  slug: "",
  title: "",
  description: "",
  emoji: "",
  coverImageUrl: "",
  interestTags: [],
  level: "starter",
  estimatedMinutes: "60",
  credentialTitle: "",
  prerequisites: [],
  sortOrder: "0",
};

function courseToForm(c: CreatorCourse): CreatorCourseValues {
  return {
    slug: c.slug,
    title: c.title,
    description: c.description || "",
    emoji: c.emoji || "",
    coverImageUrl: c.coverImageUrl || "",
    interestTags: c.interestTags || [],
    level: c.level,
    estimatedMinutes: String(c.estimatedMinutes ?? 60),
    credentialTitle: c.credentialTitle || "",
    prerequisites: c.prerequisites || [],
    sortOrder: String(c.sortOrder ?? 0),
  };
}

function formToPayload(v: CreatorCourseValues) {
  const payload: Record<string, unknown> = {
    slug: v.slug,
    title: v.title,
    description: v.description,
    emoji: v.emoji || null,
    coverImageUrl: v.coverImageUrl || null,
    category: "interest",
    interestTags: v.interestTags,
    level: v.level,
    estimatedMinutes: Number(v.estimatedMinutes),
    prerequisites: v.prerequisites,
    sortOrder: Number(v.sortOrder),
  };
  if (v.credentialTitle) payload.credentialTitle = v.credentialTitle;
  return payload;
}

// ---------------------------------------------------------------------------------
// Lesson document checks (light: the server runs the full schema)
// ---------------------------------------------------------------------------------

const CHECK_TYPES = ["question", "sort", "order", "match", "cloze", "tapword", "numberline", "fraction"];

const STARTER_DOCUMENT = `{
  "title": "How a Robot Sees the World",
  "description": "Meet the sensors a robot uses to find its way around a room.",
  "topic": "Robotics",
  "emoji": "🤖",
  "estimatedMinutes": 8,
  "xpReward": 150,
  "tags": ["robotics", "sensors"],
  "blocks": [
    {
      "type": "intro",
      "id": "b1",
      "title": "Robot Senses",
      "text": "Robots cannot see the way you do. They use **sensors**.",
      "emoji": "🤖",
      "goals": ["Name three robot sensors", "Say what each one does"]
    },
    {
      "type": "explain",
      "id": "b2",
      "heading": "What is a sensor?",
      "body": "A sensor is a part that takes in information.\\nA light sensor notices bright and dark.\\nA distance sensor notices how far away a wall is.",
      "callout": { "kind": "tip", "text": "Your eyes and ears are your own sensors." }
    },
    {
      "type": "question",
      "id": "b3",
      "kind": "mc",
      "prompt": "Which sensor helps a robot stop before it bumps a wall?",
      "options": [
        { "id": "a", "text": "Light sensor" },
        { "id": "b", "text": "Distance sensor" },
        { "id": "c", "text": "Sound sensor" }
      ],
      "answer": "b",
      "feedbackCorrect": "Yes! It measures the gap.",
      "feedbackWrong": "Think about which one measures how far away something is."
    },
    {
      "type": "summary",
      "id": "b4",
      "points": [
        "Sensors take in information from the world.",
        "A distance sensor measures the gap to an object.",
        "A light sensor notices bright and dark."
      ],
      "badgeText": "Sensor Spotter"
    }
  ]
}`;

type DocCheck = { ok: true; doc: Record<string, unknown> } | { ok: false; errors: string[] };

function checkLessonDocument(json: string): DocCheck {
  let parsed: any;
  try {
    parsed = JSON.parse(json);
  } catch (e: any) {
    return { ok: false, errors: [`This is not valid JSON yet: ${e?.message || "check your commas and brackets"}`] };
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: false, errors: ["The document must be a JSON object wrapped in { }."] };
  }

  const errors: string[] = [];
  if (typeof parsed.title !== "string" || !parsed.title.trim()) errors.push('Add a "title" for the lesson.');
  if (typeof parsed.description !== "string" || !parsed.description.trim()) {
    errors.push('Add a short "description" so students know what the lesson covers.');
  }

  if (!Array.isArray(parsed.blocks)) {
    errors.push('"blocks" must be a list of lesson blocks.');
    return { ok: false, errors };
  }

  const blocks = parsed.blocks as any[];
  if (blocks.length < 3) errors.push("A lesson needs at least 3 blocks.");
  if (blocks.length > 0 && blocks[0]?.type !== "intro") errors.push('The first block must be an "intro" block.');
  if (blocks.length > 0 && blocks[blocks.length - 1]?.type !== "summary") errors.push('The last block must be a "summary" block.');

  const seen = new Set<string>();
  blocks.forEach((b, i) => {
    if (!b || typeof b !== "object") {
      errors.push(`Block ${i + 1} is not an object.`);
      return;
    }
    if (typeof b.type !== "string") errors.push(`Block ${i + 1} is missing its "type".`);
    if (typeof b.id !== "string" || !b.id) errors.push(`Block ${i + 1} is missing an "id" such as "b${i + 1}".`);
    else if (seen.has(b.id)) errors.push(`Two blocks share the id "${b.id}". Ids must be unique.`);
    else seen.add(b.id);
  });

  const checks = blocks.filter((b) => b && CHECK_TYPES.includes(b.type)).length;
  if (checks < 1) errors.push("Add at least one check block so students can practise (question, sort, match, cloze and so on).");

  return errors.length ? { ok: false, errors } : { ok: true, doc: parsed };
}

function lessonToDocument(lesson: CreatorLesson): string {
  const doc: Record<string, unknown> = {
    title: lesson.title,
    slug: lesson.slug,
    description: lesson.description || "",
    topic: lesson.topic || "Interests",
    emoji: lesson.emoji || undefined,
    estimatedMinutes: lesson.estimatedMinutes ?? 8,
    xpReward: lesson.xpReward ?? 150,
    tags: lesson.tags || [],
    blocks: lesson.blocks || [],
  };
  return JSON.stringify(doc, null, 2);
}

// ---------------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------------

function TagPicker({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {COURSE_INTEREST_TAGS.map((tag) => {
        const on = value.includes(tag.id);
        return (
          <button
            key={tag.id}
            type="button"
            onClick={() => onChange(on ? value.filter((v) => v !== tag.id) : [...value, tag.id])}
            className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
              on ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"
            }`}
            data-testid={`tag-${tag.id}`}
          >
            <span className="mr-1">{tag.emoji}</span>
            {tag.label}
          </button>
        );
      })}
    </div>
  );
}

const BLOCK_GUIDE: { type: string; what: string; needs: string }[] = [
  { type: "intro", what: "Opens the lesson and sets the goal.", needs: "id, title, text (optional emoji, goals)" },
  { type: "explain", what: "Teaches one idea in 2 to 6 short lines.", needs: "id, heading, body (optional visual, callout)" },
  { type: "example", what: "A worked problem, one idea per step.", needs: "id, heading, problem, steps[{ text }]" },
  { type: "question", what: "Multiple choice, true or false, numeric or short text.", needs: "id, kind, prompt, answer (options for mc and multi)" },
  { type: "sort", what: "Drop items into 2 to 4 labelled bins.", needs: "id, prompt, bins[], items[]" },
  { type: "order", what: "Put 3 to 7 items in the right order.", needs: "id, prompt, items[] listed correctly" },
  { type: "match", what: "Match 2 to 6 pairs.", needs: "id, prompt, pairs[{ left, right }]" },
  { type: "cloze", what: "Fill in the blanks written as [[answer]].", needs: "id, text (optional distractors)" },
  { type: "tapword", what: "Tap the target words in a sentence.", needs: "id, prompt, sentence, targets[]" },
  { type: "numberline", what: "Drag to a spot on a number line.", needs: "id, prompt, min, max, step, target" },
  { type: "fraction", what: "Shade parts of a bar, circle or grid.", needs: "id, prompt, parts, target" },
  { type: "flashcards", what: "Vocabulary cards, front and back.", needs: "id, cards[{ front, back }]" },
  { type: "summary", what: "Closes the lesson with 3 to 5 takeaways.", needs: "id, points[] (optional badgeText)" },
];

function FormatGuide() {
  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem value="guide">
        <AccordionTrigger className="text-sm">Lesson format guide</AccordionTrigger>
        <AccordionContent>
          <div className="space-y-3 text-sm">
            <div>
              <p className="font-medium">The shape of a lesson</p>
              <p className="text-muted-foreground">
                12 to 18 blocks works best. The first block is an intro, the last is a summary, and at least 6 of the blocks
                in between should be checks using at least 4 different check types. Aim for 6 to 10 minutes of work.
              </p>
            </div>
            <div>
              <p className="font-medium">Voice</p>
              <p className="text-muted-foreground">
                Warm, playful and encouraging. Short sentences, one idea per line. Never shame a wrong answer, give a nudge
                instead. Use Jamaican context wherever it fits: JMD money, local places, food, animals and heroes.
              </p>
            </div>
            <div>
              <p className="font-medium">Rich text</p>
              <p className="text-muted-foreground">
                Text fields accept **bold**, *italic*, line breaks with \n, bullet lines starting with "- ", and inline maths
                between $...$. Write money as \$250 so it is not read as maths.
              </p>
            </div>
            <div>
              <p className="font-medium">Block types</p>
              <div className="mt-1 space-y-1">
                {BLOCK_GUIDE.map((b) => (
                  <div key={b.type} className="grid grid-cols-[90px_1fr] gap-2">
                    <code className="text-xs bg-muted rounded px-1 py-0.5 h-fit">{b.type}</code>
                    <div>
                      <p>{b.what}</p>
                      <p className="text-xs text-muted-foreground">{b.needs}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

// ---------------------------------------------------------------------------------
// Modules sheet (creator flavour: only their own lessons)
// ---------------------------------------------------------------------------------

function CreatorModulesEditor({ course, onSaved }: { course: CreatorCourse; onSaved: () => void }) {
  const { toast } = useToast();
  const { data, isLoading, error } = useQuery<{ lessons: CreatorLesson[] }>({
    queryKey: ["/api/creator/lessons"],
    queryFn: () => fetchJson<{ lessons: CreatorLesson[] }>("/api/creator/lessons"),
  });

  const saveMutation = useMutation({
    mutationFn: async (modules: { lessonId: string; required: boolean }[]) =>
      (await apiRequest("PUT", `/api/creator/courses/${course.id}/modules`, { modules })).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/creator/courses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/courses", course.slug, "modules"] });
      toast({ title: "Lessons saved", description: `${course.title} updated.` });
      onSaved();
    },
    onError: (e: unknown) => toast({ title: "Could not save lessons", description: errorMessage(e), variant: "destructive" }),
  });

  const options: ModuleLessonOption[] = (data?.lessons ?? []).map((l) => ({
    id: l.id,
    title: l.title,
    slug: l.slug,
    emoji: l.emoji,
    estimatedMinutes: l.estimatedMinutes,
    xpReward: l.xpReward,
    topic: l.topic,
    status: l.status,
  }));

  return (
    <CourseModulesEditor
      courseSlug={course.slug}
      courseTitle={course.title}
      lessonOptions={options}
      lessonsLoading={isLoading}
      lessonsError={error}
      onSave={(modules) => saveMutation.mutate(modules)}
      saving={saveMutation.isPending}
    />
  );
}

// ---------------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------------

export default function CreatorCourses() {
  const { toast } = useToast();
  const [tab, setTab] = useState("courses");
  const [courseFormOpen, setCourseFormOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<CreatorCourse | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [modulesFor, setModulesFor] = useState<CreatorCourse | null>(null);
  const [submitFor, setSubmitFor] = useState<CreatorCourse | null>(null);
  const [lessonOpen, setLessonOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState<CreatorLesson | null>(null);
  const [lessonJson, setLessonJson] = useState("");
  const [lessonErrors, setLessonErrors] = useState<string[]>([]);

  const form = useForm<CreatorCourseValues>({ resolver: zodResolver(creatorCourseSchema), defaultValues: EMPTY_COURSE });

  const coursesQuery = useQuery<{ courses: CreatorCourse[] }>({
    queryKey: ["/api/creator/courses"],
    queryFn: () => fetchJson<{ courses: CreatorCourse[] }>("/api/creator/courses"),
  });
  const courses = coursesQuery.data?.courses ?? [];

  const lessonsQuery = useQuery<{ lessons: CreatorLesson[] }>({
    queryKey: ["/api/creator/lessons"],
    queryFn: () => fetchJson<{ lessons: CreatorLesson[] }>("/api/creator/lessons"),
  });
  const lessons = lessonsQuery.data?.lessons ?? [];

  const prereqOptions = useMemo(
    () => courses.filter((c) => c.slug !== editingCourse?.slug).map((c) => ({ slug: c.slug, title: c.title })),
    [courses, editingCourse],
  );

  const saveCourse = useMutation({
    mutationFn: async (values: CreatorCourseValues) => {
      const payload = formToPayload(values);
      const res = editingCourse
        ? await apiRequest("PATCH", `/api/creator/courses/${editingCourse.id}`, payload)
        : await apiRequest("POST", "/api/creator/courses", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/creator/courses"] });
      setCourseFormOpen(false);
      setEditingCourse(null);
      toast({ title: editingCourse ? "Course saved" : "Course created", description: "It stays a draft until you submit it." });
    },
    onError: (e: unknown) => toast({ title: "Could not save the course", description: errorMessage(e), variant: "destructive" }),
  });

  const submitCourse = useMutation({
    mutationFn: async (id: string) => (await apiRequest("POST", `/api/creator/courses/${id}/submit`)).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/creator/courses"] });
      setSubmitFor(null);
      toast({ title: "Sent for review", description: "The Academy team will take a look and get back to you." });
    },
    onError: (e: unknown) => toast({ title: "Could not submit the course", description: errorMessage(e), variant: "destructive" }),
  });

  const saveLesson = useMutation({
    mutationFn: async (document: Record<string, unknown>) => {
      const res = editingLesson
        ? await apiRequest("PATCH", `/api/creator/lessons/${editingLesson.id}`, { document })
        : await apiRequest("POST", "/api/creator/lessons", { document });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/creator/lessons"] });
      setLessonOpen(false);
      setEditingLesson(null);
      toast({ title: editingLesson ? "Lesson saved" : "Lesson created" });
    },
    onError: (e: unknown) => setLessonErrors([errorMessage(e, "The lesson could not be saved")]),
  });

  const openCreateCourse = () => {
    setEditingCourse(null);
    setSlugTouched(false);
    form.reset(EMPTY_COURSE);
    setCourseFormOpen(true);
  };

  const openEditCourse = (course: CreatorCourse) => {
    setEditingCourse(course);
    setSlugTouched(true);
    form.reset(courseToForm(course));
    setCourseFormOpen(true);
  };

  const openNewLesson = () => {
    setEditingLesson(null);
    setLessonJson(STARTER_DOCUMENT);
    setLessonErrors([]);
    setLessonOpen(true);
  };

  const openLesson = (lesson: CreatorLesson) => {
    setEditingLesson(lesson);
    setLessonJson(lessonToDocument(lesson));
    setLessonErrors(
      lesson.blocks && lesson.blocks.length > 0
        ? []
        : ["This lesson's blocks were not sent with the list. Paste the full document below before saving, or you will replace it with an empty lesson."],
    );
    setLessonOpen(true);
  };

  return (
    <div className="container mx-auto p-4 max-w-5xl space-y-6 pb-24 md:pb-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" /> My Courses
          </h1>
          <p className="text-muted-foreground text-sm">
            Build a short course around something you love. Write the lessons, put them in order, then send the course to the
            Academy team for review.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="courses" data-testid="tab-courses">
            Courses
          </TabsTrigger>
          <TabsTrigger value="lessons" data-testid="tab-lessons">
            Lessons
          </TabsTrigger>
        </TabsList>

        {/* Courses -------------------------------------------------------------- */}
        <TabsContent value="courses" className="space-y-4 pt-4">
          <div className="flex justify-end">
            <Button onClick={openCreateCourse} data-testid="button-new-course">
              <Plus className="w-4 h-4 mr-2" /> New course
            </Button>
          </div>

          {coursesQuery.isLoading ? (
            <div className="grid md:grid-cols-2 gap-4">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-44 w-full" />
              ))}
            </div>
          ) : coursesQuery.error ? (
            <Card>
              <CardContent className="py-10 text-center space-y-1">
                <p className="font-medium text-destructive">Could not load your courses</p>
                <p className="text-sm text-muted-foreground">{(coursesQuery.error as Error)?.message || "Unknown error"}</p>
                <p className="text-sm text-muted-foreground">
                  This area is for EduFocal creators. If your creator application is still pending, it will open up once it is
                  approved.
                </p>
              </CardContent>
            </Card>
          ) : courses.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center space-y-2">
                <p className="text-3xl">🎓</p>
                <p className="font-medium">You have not built a course yet</p>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Start with a title and a one line description. You can add lessons and change everything before you submit
                  it for review.
                </p>
                <Button onClick={openCreateCourse} className="mt-2">
                  <Plus className="w-4 h-4 mr-2" /> Create your first course
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {courses.map((c) => {
                const meta = COURSE_STATUS_META[c.status] || COURSE_STATUS_META.draft;
                const editable = EDITABLE_STATUSES.includes(c.status);
                return (
                  <Card key={c.id} data-testid={`card-course-${c.slug}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0">
                          <span className="text-2xl">{c.emoji || "🎓"}</span>
                          <div className="min-w-0">
                            <CardTitle className="text-base truncate">{c.title}</CardTitle>
                            <CardDescription className="line-clamp-2">{c.description}</CardDescription>
                          </div>
                        </div>
                        <Badge className={`border-0 shrink-0 ${meta.className}`}>{meta.label}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-sm text-muted-foreground">{meta.blurb}</p>

                      {c.status === "changes_requested" && c.reviewNote && (
                        <div className="rounded-md border border-orange-300/60 bg-orange-50 dark:bg-orange-950/30 p-3 text-sm">
                          <p className="font-medium">Note from the review team</p>
                          <p className="text-muted-foreground whitespace-pre-wrap">{c.reviewNote}</p>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-1.5">
                        {(c.interestTags || []).map((t) => (
                          <Badge key={t} variant="outline" className="text-[11px]">
                            {interestTagLabel(t)}
                          </Badge>
                        ))}
                      </div>

                      <p className="text-xs text-muted-foreground">
                        {c.modules} lesson{c.modules === 1 ? "" : "s"} · {c.level} · {c.estimatedMinutes} min
                      </p>

                      <div className="flex flex-wrap gap-2 pt-1">
                        <Button size="sm" variant="outline" onClick={() => openEditCourse(c)} disabled={!editable}>
                          Edit details
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setModulesFor(c)} disabled={!editable} data-testid={`button-modules-${c.slug}`}>
                          <Layers className="w-4 h-4 mr-1" /> Lessons
                        </Button>
                        {editable && (
                          <Button size="sm" onClick={() => setSubmitFor(c)} data-testid={`button-submit-${c.slug}`}>
                            <Send className="w-4 h-4 mr-1" /> Submit for review
                          </Button>
                        )}
                        {c.status === "published" && (
                          <Button size="sm" variant="outline" asChild>
                            <Link href={`/courses/${c.slug}`}>View course</Link>
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Lessons -------------------------------------------------------------- */}
        <TabsContent value="lessons" className="space-y-4 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              Lessons are written as a JSON document. Start from the template, change the words, then save.
            </p>
            <Button onClick={openNewLesson} data-testid="button-new-lesson">
              <Plus className="w-4 h-4 mr-2" /> New lesson
            </Button>
          </div>

          {lessonsQuery.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : lessonsQuery.error ? (
            <Card>
              <CardContent className="py-10 text-center space-y-1">
                <p className="font-medium text-destructive">Could not load your lessons</p>
                <p className="text-sm text-muted-foreground">{(lessonsQuery.error as Error)?.message || "Unknown error"}</p>
              </CardContent>
            </Card>
          ) : lessons.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center space-y-2">
                <p className="text-3xl">📘</p>
                <p className="font-medium">No lessons yet</p>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  A course needs lessons. Open the template, keep the shape, and swap in your own words and questions.
                </p>
                <Button onClick={openNewLesson} className="mt-2">
                  <Plus className="w-4 h-4 mr-2" /> Write your first lesson
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {lessons.map((l) => (
                <Card key={l.id} data-testid={`row-lesson-${l.slug}`}>
                  <CardContent className="flex items-center gap-3 py-3">
                    <span className="text-xl">{l.emoji || "📘"}</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{l.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {[l.slug, l.topic, typeof l.blockCount === "number" ? `${l.blockCount} blocks` : null, l.status]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openLesson(l)} data-testid={`button-edit-lesson-${l.slug}`}>
                      <FileJson className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Course form ------------------------------------------------------------- */}
      <Dialog open={courseFormOpen} onOpenChange={(o) => { setCourseFormOpen(o); if (!o) setEditingCourse(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCourse ? `Edit ${editingCourse.title}` : "New course"}</DialogTitle>
            <DialogDescription>
              Every creator course is an interest course. Students who like these topics will see it in their
              recommendations once it is published.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit((values) => saveCourse.mutate(values))}>
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Build Your First Robot"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          if (!slugTouched) form.setValue("slug", slugifyTitle(e.target.value), { shouldValidate: true });
                        }}
                        data-testid="input-title"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>What will students learn?</FormLabel>
                    <FormControl>
                      <Textarea rows={3} placeholder="By the end you will have built a robot that follows a line." {...field} data-testid="input-description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Web address</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="build-your-first-robot"
                          {...field}
                          onChange={(e) => {
                            setSlugTouched(true);
                            field.onChange(e);
                          }}
                          data-testid="input-slug"
                        />
                      </FormControl>
                      <FormDescription>Filled in from your title. Change it if you like.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="emoji"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Emoji</FormLabel>
                      <FormControl>
                        <Input placeholder="🤖" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="interestTags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Interests</FormLabel>
                    <FormControl>
                      <TagPicker value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormDescription>Pick the interests a student would tick to be shown this course.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="level"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Level</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger data-testid="select-level">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {COURSE_LEVELS.map((l) => (
                            <SelectItem key={l.id} value={l.id}>
                              {l.label} · {l.hint}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="estimatedMinutes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minutes in total</FormLabel>
                      <FormControl>
                        <Input inputMode="numeric" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sortOrder"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sort order</FormLabel>
                      <FormControl>
                        <Input inputMode="numeric" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="credentialTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Credential title</FormLabel>
                      <FormControl>
                        <Input placeholder="Junior Robotics Builder" {...field} />
                      </FormControl>
                      <FormDescription>What the certificate says. Leave blank to use the course title.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="coverImageUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cover image URL</FormLabel>
                      <FormControl>
                        <Input placeholder="https://..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="prerequisites"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Take this after</FormLabel>
                    <FormControl>
                      {prereqOptions.length === 0 ? (
                        <p className="text-sm text-muted-foreground">You have no other courses to require yet.</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {prereqOptions.map((o) => {
                            const on = field.value.includes(o.slug);
                            return (
                              <button
                                key={o.slug}
                                type="button"
                                onClick={() =>
                                  field.onChange(on ? field.value.filter((v: string) => v !== o.slug) : [...field.value, o.slug])
                                }
                                className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                                  on ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"
                                }`}
                              >
                                {o.title}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </FormControl>
                    <FormDescription>Courses a student should finish first. Leave empty if it stands alone.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCourseFormOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saveCourse.isPending} data-testid="button-save-course">
                  {saveCourse.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editingCourse ? "Save changes" : "Create course"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Lesson workspace -------------------------------------------------------- */}
      <Sheet open={lessonOpen} onOpenChange={(o) => { setLessonOpen(o); if (!o) setEditingLesson(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto">
          <SheetHeader className="space-y-1">
            <SheetTitle className="flex items-center gap-2">
              <FileJson className="w-5 h-5" /> {editingLesson ? `Edit ${editingLesson.title}` : "New lesson"}
            </SheetTitle>
            <SheetDescription>
              Keep the shape of the document and change the words. Your lesson is checked here and again on the server before
              it saves.
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-3 pt-4">
            <FormatGuide />

            <div className="flex items-center justify-between">
              <Label htmlFor="lesson-json">Lesson document</Label>
              {!editingLesson && (
                <Button size="sm" variant="ghost" onClick={() => { setLessonJson(STARTER_DOCUMENT); setLessonErrors([]); }}>
                  Reset to template
                </Button>
              )}
            </div>
            <Textarea
              id="lesson-json"
              value={lessonJson}
              onChange={(e) => setLessonJson(e.target.value)}
              className="font-mono text-xs h-[50vh]"
              spellCheck={false}
              data-testid="input-lesson-json"
            />

            {lessonErrors.length > 0 && (
              <div className="rounded-md bg-destructive/10 p-3 space-y-1" data-testid="lesson-errors">
                {lessonErrors.map((e, i) => (
                  <p key={i} className="text-xs text-destructive whitespace-pre-wrap">
                    {e}
                  </p>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setLessonOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const result = checkLessonDocument(lessonJson);
                  if (!result.ok) {
                    setLessonErrors(result.errors);
                    return;
                  }
                  setLessonErrors([]);
                  saveLesson.mutate(result.doc);
                }}
                disabled={saveLesson.isPending}
                data-testid="button-save-lesson"
              >
                {saveLesson.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : "Check and save"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Modules sheet ----------------------------------------------------------- */}
      <Sheet open={!!modulesFor} onOpenChange={(o) => { if (!o) setModulesFor(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {modulesFor && <CreatorModulesEditor key={modulesFor.id} course={modulesFor} onSaved={() => setModulesFor(null)} />}
        </SheetContent>
      </Sheet>

      {/* Submit for review ------------------------------------------------------- */}
      <AlertDialog open={!!submitFor} onOpenChange={(o) => { if (!o) setSubmitFor(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send this course for review?</AlertDialogTitle>
            <AlertDialogDescription>
              {submitFor?.title} goes to the Academy team. You will not be able to edit it while it is being reviewed. If they
              ask for changes it comes straight back to you with a note.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => submitFor && submitCourse.mutate(submitFor.id)}
              data-testid="button-confirm-submit"
            >
              Submit for review
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> What the statuses mean
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(["draft", "pending_review", "changes_requested", "published"] as CourseStatus[]).map((s) => (
            <div key={s} className="flex items-start gap-2">
              <Badge className={`border-0 shrink-0 ${COURSE_STATUS_META[s].className}`}>{COURSE_STATUS_META[s].label}</Badge>
              <p className="text-sm text-muted-foreground">{COURSE_STATUS_META[s].blurb}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
